import { apiClient as api } from './apiClient';

// ═══════════════════════════════════════════════════════════════
// Módulo FERIAS (07_MODULOS/FERIAS) — contrato del backend
// (FeriasController y FeriasCasosController, api/ferias). Recurso
// ACL único: Clientes.Altas.Gestionar (D-FE-12). El usuario sale
// siempre del token. Las fechas viajan como días yyyy-MM-dd.
// ═══════════════════════════════════════════════════════════════

export const RECURSO_FERIAS = 'Clientes.Altas.Gestionar';

/** Una feria, como la devuelve GET /api/ferias. */
export interface Feria {
  id: number;
  nombre: string;
  /** yyyy-MM-dd */
  inicio: string;
  fin: string;
  activa: boolean;
}

/** Por qué la API rechazó un archivo; `error-almacenamiento` si no pudo guardarlo. */
export type MotivoRechazo =
  | 'no-pdf'
  | 'ilegible'
  | 'paginas'
  | 'demasiado-grande'
  | 'nombre-demasiado-largo'
  | 'error-almacenamiento';

/** El resultado de un archivo cargado: el caso creado, o el motivo del rechazo. */
export interface ResultadoCarga {
  nombreArchivo: string;
  idCaso: number | null;
  rechazo: MotivoRechazo | null;
  duplicadoDe: number[];
}

/** Un paso iniciado del avance de un caso. */
export interface PasoBandeja {
  idPaso: number;
  paso: string;
  idEstado: number;
  estado: string;
}

/** El progreso de lectura de un caso (diseño M3 §3 de fct-ferias). */
export type ProgresoLectura = 'esperando' | 'porVerificar' | 'verificado' | 'verificadoNuevaLectura' | 'fallida';

/**
 * Un caso como lo lista la bandeja. El aviso, la fecha de reunión y la prioridad son `null` hasta que
 * una verificación los rellena; el cliente, hasta que se guarda su paso. El código y el nombre del
 * cliente salen de Gescom, y son `null` donde la API no tiene Gescom (D-FE-30).
 */
export interface CasoBandeja {
  id: number;
  idFeria: number;
  feria: string;
  aviso: string | null;
  /** yyyy-MM-dd */
  fechaReunion: string | null;
  idCliente: number | null;
  prioridad: boolean | null;
  idEstado: number;
  estado: string;
  avance: PasoBandeja[];
  /** Instante ISO con zona. */
  fechaEntrada: string;
  nombreArchivo: string;
  posiblesDuplicados: number[];
  lectura: ProgresoLectura;
  codigoCliente?: string | null;
  nombreCliente?: string | null;
}

/** Una página de la bandeja. */
export interface PaginaBandeja {
  elementos: CasoBandeja[];
  total: number;
  pagina: number;
  tamanioPagina: number;
}

/** Los filtros de la bandeja; todos opcionales. Las fechas son días yyyy-MM-dd. */
export interface FiltroBandeja {
  idFeria?: number;
  entradaDesde?: string;
  entradaHasta?: string;
  reunionDesde?: string;
  reunionHasta?: string;
  prioridad?: boolean;
  idEstado?: number;
  lectura?: ProgresoLectura;
}

/** Si la lectura automática está encendida y, si no, por qué. */
export interface DisponibilidadLectura {
  habilitada: boolean;
  descripcion: string;
}

const DIA = /^\d{4}-\d{2}-\d{2}$/;

/** Las ferias, solo las activas si se pide. */
export const listarFerias = async (soloActivas: boolean): Promise<Feria[]> =>
  (await api.get<Feria[]>('/api/ferias', { params: { soloActivas } })).data;

/** Si la lectura automática está encendida (la bandeja lo avisa cuando no). */
export const leerDisponibilidadLectura = async (): Promise<DisponibilidadLectura> =>
  (await api.get<DisponibilidadLectura>('/api/ferias/lectura')).data;

/**
 * Una página de la bandeja. Solo viajan los filtros con valor, y las fechas como días yyyy-MM-dd: la
 * API lee cualquier otro formato con la cultura invariante, y «12/10/2026» sería el 10 de diciembre.
 */
export const listarCasos = async (filtro: FiltroBandeja, pagina: number, tamanioPagina: number): Promise<PaginaBandeja> => {
  const params: Record<string, string | number | boolean> = { pagina, tamanioPagina };
  Object.entries(filtro).forEach(([clave, valor]) => {
    if (valor === undefined || valor === '') {
      return;
    }
    if (clave.includes('Desde') || clave.includes('Hasta')) {
      if (!DIA.test(String(valor))) {
        throw new Error(`La fecha ${clave} tiene que ser un día aaaa-mm-dd: «${String(valor)}».`);
      }
    }
    params[clave] = valor;
  });
  return (await api.get<PaginaBandeja>('/api/ferias/casos', { params })).data;
};

/**
 * Carga los archivos en una feria: un resultado por archivo, en orden. El tipo multipart va explícito:
 * con el `application/json` por defecto de `apiClient`, axios convierte el FormData en JSON y los PDF no
 * llegan (QA en la PR #94). Con él, axios manda el FormData y el navegador pone el separador.
 */
export const cargarFichas = async (idFeria: number, archivos: File[]): Promise<ResultadoCarga[]> => {
  const formulario = new FormData();
  archivos.forEach((archivo) => formulario.append('archivos', archivo, archivo.name));
  return (
    await api.post<ResultadoCarga[]>(`/api/ferias/${idFeria}/casos`, formulario, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  ).data;
};

/** Elimina un caso de forma lógica: sale de la bandeja y su ficha se conserva. */
export const eliminarCaso = async (idCaso: number): Promise<void> => {
  await api.delete(`/api/ferias/casos/${idCaso}`);
};

/** El PDF de un caso. Se pide con el token, como cualquier otra llamada, y se abre como blob. */
export const descargarDocumento = async (idCaso: number): Promise<Blob> =>
  (await api.get<Blob>(`/api/ferias/casos/${idCaso}/documento`, { responseType: 'blob' })).data;

// ------------------------------------------------------------------ lectura y verificación (FERIAS-T08)

/** Una persona o entidad relacionada, y cómo se relaciona. */
export interface Relacion {
  persona: string;
  tipo: string;
}

/** El valor de un campo, en una de las cuatro formas del contrato v1 (D-FE-28). */
export type ValorCampo = string | boolean | string[] | Relacion[];

/** Cómo leyó el modelo un campo: los estados del contrato, sin traducir (D-FE-28 c). */
export type EstadoCampo = 'empty' | 'read' | 'doubtful';

/** Un campo tal como lo devolvió la lectura. */
export interface CampoLeido {
  value: ValorCampo;
  status: EstadoCampo;
  note: string;
}

/** La lectura, los intentos y la verificación vigente de un caso (GET /api/ferias/casos/{id}/lectura). */
export interface LecturaCaso {
  idCaso: number;
  progreso: ProgresoLectura;
  lectura: {
    id: number;
    modelo: string;
    versionContrato: string;
    leidaEn: string;
    campos: Record<string, CampoLeido>;
  } | null;
  intentos: { total: number; fallidos: number; ultimoError: string | null };
  verificacion: {
    id: number;
    idLectura: number | null;
    verificadaEn: string;
    usuario: string;
    valores: Record<string, ValorCampo>;
  } | null;
}

/** La lectura de un caso, con su progreso y su verificación vigente. */
export const leerLecturaCaso = async (idCaso: number): Promise<LecturaCaso> =>
  (await api.get<LecturaCaso>(`/api/ferias/casos/${idCaso}/lectura`)).data;

/**
 * Confirma todos los campos del caso a la vez (M3-04): `idLectura` es la lectura de la que salen, o
 * `null` si se teclearon a mano. El supervisor lo pone la API, del token.
 */
export const confirmarVerificacion = async (idCaso: number, idLectura: number | null, valores: Record<string, ValorCampo>): Promise<void> => {
  await api.put(`/api/ferias/casos/${idCaso}/verificacion`, { idLectura, valores });
};

/** Pide una lectura nueva del caso (P3): la registra, y la hará el lector cuando esté encendido. */
export const solicitarLectura = async (idCaso: number): Promise<void> => {
  await api.post(`/api/ferias/casos/${idCaso}/lectura`);
};

// ------------------------------------------------------------------ paso de cliente (FERIAS-T09)

/** El paso de cliente de un caso: si está guardado y con qué cliente (GET /api/ferias/casos/{id}/cliente). */
export interface ClienteCaso {
  guardado: boolean;
  idCliente: number | null;
  codigo: string | null;
  nombreCompleto: string | null;
}

/** Un criterio por el que se buscan los clientes de Gescom. */
export type Criterio = 'codigo' | 'email' | 'telefono' | 'movil' | 'nombre';

/** Los criterios de una búsqueda; cualquiera puede ir vacío. */
export type CriteriosCliente = Record<Criterio, string | null>;

/** Un cliente encontrado por la búsqueda, con los criterios con los que coincide. */
export interface CandidatoCliente {
  id: number;
  codigo: string | null;
  nombreCompleto: string;
  provincia: string | null;
  telefonoPrincipal: string | null;
  emailPrincipal: string | null;
  coincide: Criterio[];
}

/** Los candidatos de un caso: los criterios, las coincidencias por criterio y hasta 50 clientes. */
export interface BusquedaCliente {
  criterios: CriteriosCliente;
  coincidencias: Record<Criterio, number>;
  candidatos: CandidatoCliente[];
  /** Si puede haber más coincidencias que candidatos mostrados. */
  puedeEstarCortada: boolean;
}

/** Un cliente por su código (D35). */
export interface ResumenCliente {
  id: number;
  codigo: string | null;
  nombreCompleto: string;
  provincia: string | null;
}

/** Un campo lado a lado: el valor actual en Gescom y el verificado de la ficha. */
export interface CampoComparado {
  clave: string;
  actual: string | null;
  ficha: string | null;
  distinto: boolean;
  escribible: boolean;
}

/** Las dos selecciones del perfil de compra (CA09), como ids de catálogo. */
export interface ComparacionPerfil {
  plazoActual: number | null;
  plazoFicha: number | null;
  tramosActuales: number[];
  tramosFicha: number[];
  provinciasActuales: number[];
  provinciasFicha: number[];
  /** Las provincias escritas en la ficha que no corresponden a ninguna de Gescom. */
  provinciasSinCorrespondencia: string[];
  /** El inversor está dado de baja: guardar un perfil lo reactiva. */
  dadoDeBaja: boolean;
}

/** Un cliente comparado con los valores verificados del caso, con el token que devuelve el guardado. */
export interface ComparacionCliente {
  idCliente: number;
  codigo: string | null;
  nombreCompleto: string;
  campos: CampoComparado[];
  perfil: ComparacionPerfil;
  /** Valores que se muestran como contexto y nunca se escriben: la farmacia y las relaciones. */
  contexto: Record<string, unknown>;
  token: string;
}

/** Un perfil de compra como lo recibe el guardado. */
export interface SeleccionPerfil {
  plazo: number | null;
  tramos: number[];
  provincias: number[];
}

/** Lo que manda la actualización (PUT …/cliente): los valores de la columna de Gescom que difieren de los actuales. */
export interface ActualizacionCliente {
  idCliente: number;
  valores: Record<string, string>;
  /** `null` si el supervisor conserva el perfil de Gescom (D32). */
  perfil: SeleccionPerfil | null;
  token: string;
  /** El id del guardado: el mismo id otra vez no vuelve a escribir (D39, CA21). */
  idPeticion: string;
}

/** Lo que manda el alta (POST …/cliente): la columna de Gescom. */
export interface ClienteNuevo {
  valores: Record<string, string>;
  perfil: SeleccionPerfil;
  /** El id del guardado: el mismo id otra vez no vuelve a escribir (D39, CA21). */
  idPeticion: string;
}

/** Un paso de cliente guardado. */
export interface ClienteGuardado {
  idCliente: number;
  /** El paso ya estaba guardado por ese mismo guardado: no se ha vuelto a escribir nada (CA21). */
  yaGuardado: boolean;
}

/** El paso de cliente de un caso. */
export const leerClienteCaso = async (idCaso: number): Promise<ClienteCaso> =>
  (await api.get<ClienteCaso>(`/api/ferias/casos/${idCaso}/cliente`)).data;

/** Los candidatos de un caso: por sus valores verificados o, si se da alguno, por los criterios dados. */
export const buscarClientes = async (idCaso: number, aMano: Partial<CriteriosCliente> = {}): Promise<BusquedaCliente> => {
  const params = Object.fromEntries(
    Object.entries(aMano)
      .filter(([, valor]) => valor !== null && valor !== undefined && valor.trim() !== '')
      .map(([clave, valor]) => [clave, (valor as string).trim()]),
  );
  return (await api.get<BusquedaCliente>(`/api/ferias/casos/${idCaso}/cliente/candidatos`, { params })).data;
};

/** Todos los clientes con exactamente este código, sin distinguir mayúsculas (D35). */
export const buscarClientesPorCodigo = async (codigo: string): Promise<ResumenCliente[]> =>
  (await api.get<ResumenCliente[]>(`/api/ferias/clientes/por-codigo/${encodeURIComponent(codigo.trim())}`)).data;

/** Un cliente comparado con los valores verificados del caso. */
export const compararCliente = async (idCaso: number, idCliente: number): Promise<ComparacionCliente> =>
  (await api.get<ComparacionCliente>(`/api/ferias/casos/${idCaso}/cliente/comparacion`, { params: { idCliente } })).data;

/** Actualiza el cliente con los valores enviados; un 409 trae la comparación actual (M4-02). */
export const actualizarCliente = async (idCaso: number, actualizacion: ActualizacionCliente): Promise<ClienteGuardado> =>
  (await api.put<ClienteGuardado>(`/api/ferias/casos/${idCaso}/cliente`, actualizacion)).data;

/** Crea el cliente con los valores dados (CA07, CA08, M4-01, D37). */
export const crearCliente = async (idCaso: number, cliente: ClienteNuevo): Promise<ClienteGuardado> =>
  (await api.post<ClienteGuardado>(`/api/ferias/casos/${idCaso}/cliente`, cliente)).data;

// ------------------------------------------------------------------ acciones (M6, #135)

/** Los estados de un caso que muestra la bandeja (M6 §4, P7); los demás del catálogo ya no se usan. */
export const ESTADO_PENDIENTE = 1;
export const ESTADO_ERROR = 5;
export const ESTADO_TRATADO = 6;

/** Los pasos del avance de los que sale lo generado (catálogo FC_C_FERIAS_PASO), y su estado Completado. */
export const PASO_ACTIVIDAD = 3;
export const PASO_OPORTUNIDAD_VENTA = 4;
export const PASO_OPORTUNIDAD_COMPRA = 5;
export const ESTADO_PASO_COMPLETADO = 2;

/** El recurso que exige generar una oportunidad: sin él, «Acciones» no muestra sus secciones (D-FE-39). */
export const RECURSO_OPORTUNIDADES = 'Oportunidades.Gestionar';

/**
 * La cabecera de «Acciones» (GET /api/ferias/casos/{id}/acciones): el resumen del caso, su estado y su
 * avance. El código y el nombre del cliente salen de Gescom, y son `null` donde la API no lo tiene.
 */
export interface AccionesCaso {
  idCaso: number;
  idFeria: number;
  feria: string;
  /** yyyy-MM-dd */
  fechaReunion: string | null;
  aviso: string | null;
  prioridad: boolean | null;
  idCliente: number | null;
  idEstado: number;
  estado: string;
  avance: PasoBandeja[];
  codigoCliente: string | null;
  nombreCliente: string | null;
  /** Los números de las oportunidades generadas desde «Acciones» (pasos 4 y 5); `null` si aún no. */
  numeroVenta: number | null;
  numeroCompra: number | null;
  /** El origen de la feria y su detalle, con los que nace una oportunidad (D-FE-42); el detalle puede faltar. */
  idOrigenFeria: number | null;
  idDetalleOrigenFeria: number | null;
}

/** La cabecera de «Acciones» de un caso; también la de un caso sin cliente, que la pantalla no deja tratar. */
export const leerAccionesCaso = async (idCaso: number): Promise<AccionesCaso> =>
  (await api.get<AccionesCaso>(`/api/ferias/casos/${idCaso}/acciones`)).data;

/** Da el caso por tratado (D-FE-38) y devuelve la cabecera actualizada; repetirlo no cambia nada. */
export const darPorTratado = async (idCaso: number): Promise<AccionesCaso> =>
  (await api.post<AccionesCaso>(`/api/ferias/casos/${idCaso}/tratado`)).data;

/** Devuelve a Pendiente un caso tratado (P6) y devuelve la cabecera actualizada. */
export const reabrirCaso = async (idCaso: number): Promise<AccionesCaso> =>
  (await api.post<AccionesCaso>(`/api/ferias/casos/${idCaso}/reabrir`)).data;

/** De qué es un elemento de la actividad del cliente. */
export type ClaseActividad = 'ACTIVIDAD' | 'VENTA' | 'COMPRA';

/** Una actividad u oportunidad del cliente: el id es el número de la oportunidad, o el de la actividad. */
export interface ActividadCliente {
  clase: ClaseActividad;
  id: number;
  /** Instante ISO con zona. */
  fecha: string;
  tipo: string;
  estado: string;
  responsable: string | null;
  /** Origen y detalle, solo en las oportunidades. */
  origen: string | null;
}

/**
 * La actividad reciente del cliente de un caso (GET …/acciones/actividad): desde la reunión menos 2 días
 * hasta hoy (días yyyy-MM-dd de Madrid). `disponible` es `false` donde la base no tiene Gescom.
 */
export interface ActividadReciente {
  desde: string;
  hasta: string;
  disponible: boolean;
  elementos: ActividadCliente[];
}

/** La actividad reciente del cliente de un caso con cliente. */
export const leerActividadCliente = async (idCaso: number): Promise<ActividadReciente> =>
  (await api.get<ActividadReciente>(`/api/ferias/casos/${idCaso}/acciones/actividad`)).data;

/** Un tipo de oportunidad que se genera desde «Acciones». */
export type TipoOportunidad = 'venta' | 'compra';

/**
 * Registra en su paso el número de la oportunidad recién guardada en la ventana de Oportunidades (M6 §3.2),
 * una sola vez por id de petición (D39); devuelve la cabecera actualizada.
 */
export const registrarOportunidad = async (idCaso: number, tipo: TipoOportunidad, numero: number, idPeticion: string): Promise<AccionesCaso> =>
  (await api.post<AccionesCaso>(`/api/ferias/casos/${idCaso}/acciones/oportunidad`, { tipo, numero, idPeticion })).data;

/** Si el caso completó un paso del avance. */
export const pasoCompletado = (avance: PasoBandeja[], idPaso: number): boolean =>
  avance.some((paso) => paso.idPaso === idPaso && paso.idEstado === ESTADO_PASO_COMPLETADO);

// ------------------------------------------------------------------ añadir actividad (M6, #137)

/** Una opción de un catálogo de actividad de Gescom. */
export interface OpcionActividad {
  id: number;
  nombre: string;
}

/** Un protocolo, con el tipo de evento y el tipo de actividad a los que pertenece (sin tipo de actividad, vale para cualquiera). */
export interface ProtocoloActividad {
  id: number;
  idTipoEvento: number;
  idTipoActividad: number | null;
  nombre: string;
}

/** Los catálogos del formulario «Añadir actividad». */
export interface CatalogosActividad {
  tiposActividad: OpcionActividad[];
  tiposEvento: OpcionActividad[];
  protocolos: ProtocoloActividad[];
  modalidades: OpcionActividad[];
  ubicaciones: OpcionActividad[];
  estados: OpcionActividad[];
}

/** Los valores editables de una actividad; el responsable y el cliente los pone el servidor. */
export interface ValoresActividad {
  idTipoActividad: number;
  idTipoEvento: number;
  /** Vacío al proponerla: es obligatorio (D13). */
  idProtocolo: number | null;
  /** Instante ISO con zona. */
  inicio: string;
  duracionMinutos: number;
  idModalidad: number;
  idUbicacion: number | null;
  idEstado: number;
  resenia: string;
}

/**
 * La propuesta de «Añadir actividad» (GET …/acciones/actividad/propuesta): los valores de M6 §3.1, el
 * responsable (`null` si el supervisor no tiene ficha de empleado) y los catálogos.
 */
export interface PropuestaActividad {
  valores: ValoresActividad;
  responsable: { id: number; nombre: string } | null;
  catalogos: CatalogosActividad;
}

/** Una actividad añadida; `yaCreada` si esa misma petición ya la había creado (D39). */
export interface ActividadAnadida {
  idActividad: number;
  yaCreada: boolean;
}

/** La propuesta de una actividad nueva para el cliente del caso. */
export const leerPropuestaActividad = async (idCaso: number): Promise<PropuestaActividad> =>
  (await api.get<PropuestaActividad>(`/api/ferias/casos/${idCaso}/acciones/actividad/propuesta`)).data;

/** Añade la actividad en Gescom, una sola vez por id de petición (D39). */
export const anadirActividad = async (idCaso: number, valores: ValoresActividad, idPeticion: string): Promise<ActividadAnadida> =>
  (await api.post<ActividadAnadida>(`/api/ferias/casos/${idCaso}/acciones/actividad`, { valores, idPeticion })).data;

/** Un id nuevo para un guardado, generado cuando el supervisor lo confirma (D39). */
export function nuevoIdPeticion(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  // Un UUID versión 4 a partir de bytes aleatorios, donde falta randomUUID (fuera de HTTPS).
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** El cuerpo de una respuesta de error de la API, si lo trae: algunos rechazos llevan datos (M4-02, D37). */
export function cuerpoError(error: unknown): Record<string, unknown> {
  const datos = (error as { response?: { data?: unknown } }).response?.data;
  return typeof datos === 'object' && datos !== null ? (datos as Record<string, unknown>) : {};
}

/** El estado HTTP de un error de la API, si lo trae. */
export function estadoError(error: unknown): number | undefined {
  return (error as { response?: { status?: number } }).response?.status;
}

/** El mensaje de un error de la API, el suyo si lo trae; si no, el del error. */
export function mensajeError(error: unknown): string {
  const respuesta = (error as { response?: { status?: number; data?: { error?: unknown } } }).response;
  if (typeof respuesta?.data?.error === 'string') {
    return respuesta.data.error;
  }
  if (respuesta?.status) {
    return `Error ${respuesta.status}`;
  }
  return error instanceof Error ? error.message : String(error);
}
